import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Hash, Type, TrendingUp, Calendar, Sparkles, Loader2,
  ArrowUpDown, Filter, Check, X, ChevronRight, ChevronDown, WandSparkles, EyeOff, Undo2, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { CertificationBadge } from '@/components/CertificationBadge';

const TYPE_ICONS = { integer: Hash, float: TrendingUp, string: Type, date: Calendar, datetime: Calendar, boolean: Check };
const SEM_COLORS = {
  dimension: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  measure: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  hidden: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20',
};
const AGG_OPTIONS = ['none', 'sum', 'avg', 'count', 'count_distinct', 'min', 'max', 'first', 'last'];
const NUMBER_FORMATS = ['', '#,##0', '#,##0.00', '$#,##0', '$#,##0.00', '#0%', '#0.0%', '0', '#,##0.0K', '$#,##0.0M'];
const DATE_FORMATS = ['', 'YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'DD MMM YYYY', 'MMM YYYY', 'YYYY-MM-DD HH:mm', 'YYYY'];
const TEXT_FORMATS = ['', 'UPPERCASE', 'lowercase', 'Title Case'];
const BOOLEAN_FORMATS = ['', 'Yes/No', 'True/False', '1/0'];
// Bulk-apply keeps the number set (bulk targets may be mixed-type; number
// masks are the most commonly applied). The per-row picker is type-aware below.
const FORMAT_OPTIONS = NUMBER_FORMATS;

const _isDateType = (t) => /date|time|timestamp/i.test(t || '');
const _isNumberType = (t) => /int|float|double|decimal|numer|number|money|real|currency/i.test(t || '');
const _isBoolType = (t) => /bool/i.test(t || '');
// Return the format masks that make sense for a field's data type, so a date or
// varchar field never offers number masks like #,##0.
function formatOptionsFor(dataType) {
  if (_isDateType(dataType)) return DATE_FORMATS;
  if (_isBoolType(dataType)) return BOOLEAN_FORMATS;
  if (_isNumberType(dataType)) return NUMBER_FORMATS;
  return TEXT_FORMATS;
}

// Render a tiny live sample of what a format mask produces, so editors can pick
// the right mask at a glance (e.g. "#,##0.00" → "1,234.57", "MMM YYYY" → "Dec 2025").
const _SAMPLE_NUM = 1234.567;
const _SAMPLE_DATE = new Date(2025, 11, 31, 14, 5); // 31 Dec 2025 14:05
const _MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatSample(mask) {
  if (!mask) return '';
  const d = _SAMPLE_DATE, p2 = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear(), mm = p2(d.getMonth() + 1), dd = p2(d.getDate());
  const hh = p2(d.getHours()), mi = p2(d.getMinutes());
  switch (mask) {
    // dates
    case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
    case 'MM/DD/YYYY': return `${mm}/${dd}/${yyyy}`;
    case 'DD/MM/YYYY': return `${dd}/${mm}/${yyyy}`;
    case 'DD MMM YYYY': return `${dd} ${_MONTHS[d.getMonth()]} ${yyyy}`;
    case 'MMM YYYY': return `${_MONTHS[d.getMonth()]} ${yyyy}`;
    case 'YYYY-MM-DD HH:mm': return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    case 'YYYY': return `${yyyy}`;
    // booleans
    case 'Yes/No': return 'Yes';
    case 'True/False': return 'True';
    case '1/0': return '1';
    // text
    case 'UPPERCASE': return 'ACME CORP';
    case 'lowercase': return 'acme corp';
    case 'Title Case': return 'Acme Corp';
    // numbers
    case '#,##0': return '1,235';
    case '#,##0.00': return '1,234.57';
    case '$#,##0': return '$1,235';
    case '$#,##0.00': return '$1,234.57';
    case '#0%': return '123457%';
    case '#0.0%': return '123456.7%';
    case '0': return '1235';
    case '#,##0.0K': return '1.2K';
    case '$#,##0.0M': return '$0.0M';
    default: return '';
  }
}
const DATA_TYPE_OPTIONS = ['string', 'integer', 'float', 'boolean', 'date', 'datetime'];

/** Common acronyms that should stay uppercase */
const ACRONYMS = new Set(['id', 'url', 'api', 'sql', 'csv', 'pdf', 'xml', 'html', 'json', 'ip', 'ui', 'ux', 'kpi', 'roi', 'ytd', 'mtd', 'yoy', 'mom', 'lob', 'fy', 'qty', 'ssn', 'ein', 'tin', 'vin', 'iso', 'usd', 'eur', 'gbp', 'cad', 'sku', 'p&c', 'gl', 'wc', 'clm', 'pnl', 'ebitda', 'cogs', 'arpu', 'ltv', 'cac', 'mrr', 'arr', 'nps', 'sla', 'eta', 'etl', 'crm', 'erp', 'hmo', 'ppo', 'eob']);

/** Parse a technical field name into a human-friendly display name */
function parseFieldName(name) {
  if (!name) return '';
  // Split by camelCase boundaries
  let result = name.replace(/([a-z])([A-Z])/g, '$1 $2');
  // Replace underscores with spaces
  result = result.replace(/_/g, ' ');
  // Process each word: lowercase all-caps words (unless acronym), then title-case
  return result.split(/\s+/).map(word => {
    const lower = word.toLowerCase();
    if (ACRONYMS.has(lower)) return word.toUpperCase();
    // Already mixed case (e.g., "McDonalds") — keep as-is
    if (word.length > 1 && word !== word.toUpperCase() && word !== word.toLowerCase()) return word;
    // All-uppercase word that's not an acronym → lowercase then capitalize first
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }).join(' ').trim();
}

export function MetadataTableView({ entities, setEntities, modelId, model, setModel, calcMeasures = [], setCalcMeasures, modelGlossary = [], setModelGlossary, onEntityDeleted }) {
  const [filter, setFilter] = useState('');
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, entityId, entityName }
  const [deletingEntityId, setDeletingEntityId] = useState(null);
  const [filterEntity, setFilterEntity] = useState('_all');
  const [filterSemType, setFilterSemType] = useState('_all');
  const [filterMissing, setFilterMissing] = useState(false);
  const [sortCol, setSortCol] = useState('entity');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(new Set());
  const [bulkSemType, setBulkSemType] = useState('');
  const [bulkAgg, setBulkAgg] = useState('');
  const [bulkFormat, setBulkFormat] = useState('');
  const [aiDescLoading, setAiDescLoading] = useState(false);
  // Collapsed entities — all collapsed by default
  const [collapsed, setCollapsed] = useState(() => new Set(entities.map(e => e.entity_id)));
  const [hidingEmpty, setHidingEmpty] = useState(false);
  const [describing, setDescribing] = useState(false);
  const [aiOptsOpen, setAiOptsOpen] = useState(false);
  const [aiOverwrite, setAiOverwrite] = useState(false);
  const [aiSamples, setAiSamples] = useState(false);
  const [lastUndo, setLastUndo] = useState(null); // { label, snapshot, at }
  const [undoBusy, setUndoBusy] = useState(false);
  // Field groups modal + active group filter
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);

  // Flatten all fields into rows
  const allRows = useMemo(() => {
    const rows = [];
    entities.forEach(entity => {
      (entity.attributes || []).forEach((attr, idx) => {
        rows.push({
          entityId: entity.entity_id,
          entityName: entity.name,
          entityLogicalName: entity.logical_name || '',
          entityType: entity.entity_type,
          attrIndex: idx,
          ...attr,
        });
      });
    });
    return rows;
  }, [entities]);

  // Filter and sort
  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (filter) {
      const q = filter.toLowerCase();
      rows = rows.filter(r => r.name?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q) || r.entityName?.toLowerCase().includes(q) || r.business_name?.toLowerCase().includes(q));
    }
    if (filterEntity !== '_all') rows = rows.filter(r => r.entityId === filterEntity);
    if (filterSemType !== '_all') rows = rows.filter(r => r.semantic_type === filterSemType);
    if (filterMissing) rows = rows.filter(r => !r.description?.trim());
    if (activeGroupId) {
      const grp = (model?.field_groups || []).find(g => g.group_id === activeGroupId);
      if (grp) {
        const set = new Set(grp.fields);
        rows = rows.filter(r => set.has(`${r.entityName}.${r.name}`));
      }
    }
    rows.sort((a, b) => {
      let va = a[sortCol] || '', vb = b[sortCol] || '';
      if (sortCol === 'entity') { va = a.entityName; vb = b.entityName; }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      return sortDir === 'asc' ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
    return rows;
  }, [allRows, filter, filterEntity, filterSemType, filterMissing, sortCol, sortDir, activeGroupId, model]);

  // Group by entity
  const groupedByEntity = useMemo(() => {
    const groups = [];
    const seen = new Set();
    for (const row of filteredRows) {
      if (!seen.has(row.entityId)) {
        seen.add(row.entityId);
        groups.push({ entityId: row.entityId, entityName: row.entityName, entityLogicalName: row.entityLogicalName, rows: [] });
      }
      groups.find(g => g.entityId === row.entityId).rows.push(row);
    }
    return groups;
  }, [filteredRows]);

  const rowKey = (r) => `${r.entityId}__${r.attrIndex}`;

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const toggleCollapse = (entityId) => {
    setCollapsed(prev => { const n = new Set(prev); n.has(entityId) ? n.delete(entityId) : n.add(entityId); return n; });
  };

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(entities.map(e => e.entity_id)));

  const updateField = useCallback(async (entityId, attrIndex, updates) => {
    const key = `${entityId}__${attrIndex}`;
    setSaving(prev => new Set(prev).add(key));
    const en = entities.find(e => e.entity_id === entityId);
    if (!en) { setSaving(prev => { const s = new Set(prev); s.delete(key); return s; }); return; }
    const na = [...(en.attributes || [])];
    na[attrIndex] = { ...na[attrIndex], ...updates };
    try {
      const u = await api.updateSemanticEntity(entityId, { attributes: na });
      setEntities(prev => prev.map(e => e.entity_id === entityId ? u : e));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(prev => { const s = new Set(prev); s.delete(key); return s; });
    }
  }, [entities, setEntities]);

  const updateEntityDisplayName = useCallback(async (entityId, value) => {
    try {
      const u = await api.updateSemanticEntity(entityId, { logical_name: value });
      setEntities(prev => prev.map(e => e.entity_id === entityId ? u : e));
    } catch (err) { toast.error(err.message); }
  }, [setEntities]);

  const handleBlurSave = (entityId, attrIndex, field, value) => {
    const original = entities.find(e => e.entity_id === entityId)?.attributes?.[attrIndex]?.[field] || '';
    if (value !== original) updateField(entityId, attrIndex, { [field]: value });
  };

  const handleDeleteEntity = useCallback(async (entityId, entityName) => {
    if (!window.confirm(`Remove table "${entityName}" from this model? Related relationships will also be removed. The underlying data source is not modified.`)) return;
    setDeletingEntityId(entityId);
    try {
      await api.deleteSemanticEntity(entityId);
      setEntities(prev => prev.filter(e => e.entity_id !== entityId));
      if (onEntityDeleted) onEntityDeleted(entityId);
      toast.success(`Removed ${entityName}`);
    } catch (err) { toast.error(err.message); }
    finally { setDeletingEntityId(null); setCtxMenu(null); }
  }, [setEntities, onEntityDeleted]);

  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [ctxMenu]);

  // Take a lightweight snapshot of every entity's attribute list so
  // the user can Undo the most recent batch mutation.
  const snapshotEntities = () => entities.map(e => ({
    entity_id: e.entity_id,
    attributes: (e.attributes || []).map(a => ({ ...a })),
  }));

  const undoLast = async () => {
    if (!lastUndo) return;
    setUndoBusy(true);
    try {
      for (const snap of lastUndo.snapshot) {
        try {
          const result = await api.updateSemanticEntity(snap.entity_id, { attributes: snap.attributes });
          setEntities(prev => prev.map(e => e.entity_id === snap.entity_id ? result : e));
        } catch (err) { toast.error(err.message); }
      }
      toast.success(`Undid ${lastUndo.label}`);
      setLastUndo(null);
    } finally { setUndoBusy(false); }
  };

  // Bulk operations
  const applyBulk = async (field, value) => {
    if (!value || selected.size === 0) return;
    const snapshot = snapshotEntities();
    const grouped = {};
    for (const key of selected) {
      const [eid, aidx] = key.split('__');
      if (!grouped[eid]) grouped[eid] = [];
      grouped[eid].push({ idx: Number(aidx), update: { [field]: value } });
    }
    for (const [eid, updates] of Object.entries(grouped)) {
      const en = entities.find(e => e.entity_id === eid);
      if (!en) continue;
      const na = [...(en.attributes || [])];
      for (const u of updates) na[u.idx] = { ...na[u.idx], ...u.update };
      try {
        const result = await api.updateSemanticEntity(eid, { attributes: na });
        setEntities(prev => prev.map(e => e.entity_id === eid ? result : e));
      } catch (err) { toast.error(err.message); }
    }
    setLastUndo({ label: `Bulk edit (${field})`, snapshot, at: Date.now() });
    toast.success(`Updated ${selected.size} fields`);
    // Reset bulk dropdowns but keep selection (Fix #8 & #9)
    setBulkSemType('');
    setBulkAgg('');
    setBulkFormat('');
  };

  // Parse all field names to display names (for checked rows or all)
  const parseFieldNames = async () => {
    const targets = selected.size > 0 ? filteredRows.filter(r => selected.has(rowKey(r))) : filteredRows;
    if (targets.length === 0) return;
    const snapshot = snapshotEntities();
    const grouped = {};
    for (const row of targets) {
      if (!grouped[row.entityId]) grouped[row.entityId] = [];
      grouped[row.entityId].push({ idx: row.attrIndex, update: { business_name: parseFieldName(row.name) } });
    }
    for (const [eid, updates] of Object.entries(grouped)) {
      const en = entities.find(e => e.entity_id === eid);
      if (!en) continue;
      const na = [...(en.attributes || [])];
      for (const u of updates) na[u.idx] = { ...na[u.idx], ...u.update };
      try {
        const result = await api.updateSemanticEntity(eid, { attributes: na });
        setEntities(prev => prev.map(e => e.entity_id === eid ? result : e));
      } catch (err) { toast.error(err.message); }
    }
    setLastUndo({ label: 'Parse Names', snapshot, at: Date.now() });
    toast.success(`Parsed ${targets.length} field names into display names`);
  };

  // AI describe selected fields (with sample data)
  const aiDescribeSelected = async () => {
    const targets = selected.size > 0 ? filteredRows.filter(r => selected.has(rowKey(r))) : [];
    if (targets.length === 0) { toast.error('Select fields to describe with AI'); return; }
    setAiDescLoading(true);
    try {
      const fieldNames = targets.map(r => ({ entity: r.entityName, field: r.name, type: r.data_type, semantic: r.semantic_type }));
      const result = await api.aiDescribeFields(modelId, fieldNames);
      if (result?.descriptions) {
        const grouped = {};
        for (const [rawKey, desc] of Object.entries(result.descriptions)) {
          // Handle both "field" and "Entity.field" key formats
          const fieldName = rawKey.includes('.') ? rawKey.split('.').pop() : rawKey;
          for (const row of targets) {
            if (row.name === fieldName || row.name === rawKey) {
              if (!grouped[row.entityId]) grouped[row.entityId] = [...(entities.find(e => e.entity_id === row.entityId)?.attributes || [])];
              grouped[row.entityId][row.attrIndex] = { ...grouped[row.entityId][row.attrIndex], description: desc };
            }
          }
        }
        for (const [eid, attrs] of Object.entries(grouped)) {
          try {
            const u = await api.updateSemanticEntity(eid, { attributes: attrs });
            // Force new object reference to trigger React re-render
            setEntities(prev => prev.map(e => e.entity_id === eid ? { ...u } : e));
          } catch {}
        }
        toast.success(`AI generated ${Object.keys(result.descriptions).length} descriptions`);
      }
    } catch (err) {
      toast.error('AI description failed: ' + err.message);
    } finally {
      setAiDescLoading(false);
    }
  };

  const toggleAll = () => {
    const allKeys = filteredRows.map(rowKey);
    if (selected.size === allKeys.length) setSelected(new Set());
    else setSelected(new Set(allKeys));
  };

  const toggleEntityRows = (entityId, check) => {
    const keys = filteredRows.filter(r => r.entityId === entityId).map(rowKey);
    setSelected(prev => {
      const n = new Set(prev);
      keys.forEach(k => check ? n.add(k) : n.delete(k));
      return n;
    });
  };

  const SortHeader = ({ col, label }) => (
    <button onClick={() => toggleSort(col)} className="flex items-center gap-1 text-[9px] text-muted-foreground uppercase tracking-wider font-semibold hover:text-foreground transition-colors">
      {label}
      <ArrowUpDown className={`w-2.5 h-2.5 ${sortCol === col ? 'text-primary' : 'text-muted-foreground/30'}`} />
    </button>
  );

  const COL_SPAN = 10;

  return (
    <div className="flex flex-col h-full" data-testid="metadata-table-view">
      {/* Toolbar */}
      <div className="px-4 py-2 border-b border-border/30 flex items-center gap-3 flex-wrap" style={{ background: 'var(--glass-bg)' }}>
        <div className="relative">
          <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search fields..." className="h-7 w-44 pl-7 text-[10px] rounded-lg bg-muted/30 border-border/30" data-testid="meta-search" />
        </div>
        <Select value={filterEntity} onValueChange={setFilterEntity}>
          <SelectTrigger className="h-7 w-40 text-[10px] rounded-lg bg-muted/30 border-border/30" data-testid="meta-filter-entity"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Entities</SelectItem>
            {entities.map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSemType} onValueChange={setFilterSemType}>
          <SelectTrigger className="h-7 w-32 text-[10px] rounded-lg bg-muted/30 border-border/30" data-testid="meta-filter-sem"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Types</SelectItem>
            <SelectItem value="dimension">Dimensions</SelectItem>
            <SelectItem value="measure">Measures</SelectItem>
            <SelectItem value="hidden">Hidden</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={() => setFilterMissing(m => !m)} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] border transition-colors ${filterMissing ? 'bg-amber-500/15 border-amber-500/30 text-amber-400' : 'bg-muted/20 border-border/30 text-muted-foreground hover:text-foreground'}`} data-testid="meta-filter-missing">
          Missing Desc
        </button>
        <div className="flex gap-1 ml-1">
          <button onClick={expandAll} className="px-1.5 py-0.5 text-[9px] rounded border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors" data-testid="expand-all">Expand All</button>
          <button onClick={collapseAll} className="px-1.5 py-0.5 text-[9px] rounded border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors" data-testid="collapse-all">Collapse All</button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={parseFieldNames} className="rounded-lg text-[10px] h-7 gap-1" title="Parse field names into display names (replace _ with space, title case)" data-testid="parse-names-btn">
            <WandSparkles className="w-3 h-3" />
            Parse Names
          </Button>
          <Popover open={aiOptsOpen} onOpenChange={setAiOptsOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" disabled={describing}
                className="rounded-lg text-[10px] h-7 gap-1"
                title="Use AI to write plain-English descriptions for fields"
                data-testid="ai-describe-btn">
                <Sparkles className="w-3 h-3" />
                {describing ? 'Describing…' : 'AI Descriptions'}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3 space-y-2" data-testid="ai-describe-popover">
              <p className="text-[11px] font-medium">Generate AI descriptions</p>
              <label className="flex items-start gap-2 text-[10px] cursor-pointer">
                <input type="checkbox" checked={aiOverwrite} onChange={e => setAiOverwrite(e.target.checked)}
                  className="mt-0.5" data-testid="ai-desc-overwrite" />
                <span><b>Overwrite existing descriptions.</b><br />Otherwise only blank fields are filled.</span>
              </label>
              <label className="flex items-start gap-2 text-[10px] cursor-pointer">
                <input type="checkbox" checked={aiSamples} onChange={e => setAiSamples(e.target.checked)}
                  className="mt-0.5" data-testid="ai-desc-samples" />
                <span><b>Include sample rows.</b><br />Feeds a few real data rows into the AI prompt so descriptions reference actual values (slower + higher token cost).</span>
              </label>
              <Button
                size="sm" disabled={describing}
                onClick={async () => {
                  if (!modelId) return;
                  setAiOptsOpen(false);
                  setDescribing(true);
                  try {
                    // Snapshot for Undo
                    const snapshot = snapshotEntities();
                    const result = await api.aiDescribeFieldsBulk(modelId, { overwrite: aiOverwrite, include_samples: aiSamples });
                    toast.success(`Described ${result.described_count} field${result.described_count === 1 ? '' : 's'}`);
                    if (result.described_count > 0) {
                      setEntities(prev => prev.map(e => {
                        const patch = result.described.filter(d => d.entity_id === e.entity_id);
                        if (!patch.length) return e;
                        const byName = Object.fromEntries(patch.map(p => [p.field, p.description]));
                        return { ...e, attributes: (e.attributes || []).map(a => byName[a.name] ? { ...a, description: byName[a.name] } : a) };
                      }));
                      setLastUndo({ label: 'AI Descriptions', snapshot, at: Date.now() });
                    }
                  } catch (err) { toast.error(err.message || 'AI description failed'); }
                  finally { setDescribing(false); }
                }}
                className="w-full h-7 text-[10px] bg-primary text-primary-foreground"
                data-testid="ai-desc-run">
                {describing ? 'Running…' : 'Run'}
              </Button>
            </PopoverContent>
          </Popover>
          <Button
            variant="outline" size="sm" disabled={hidingEmpty}
            onClick={async () => {
              if (!modelId) return;
              if (!window.confirm('Scan every field for distinct values and hide any with 0 or 1 unique value?')) return;
              const snapshot = snapshotEntities();
              setHidingEmpty(true);
              try {
                const result = await api.hideEmptyFields(modelId);
                toast.success(`Hid ${result.hidden_count} empty field${result.hidden_count === 1 ? '' : 's'} out of ${result.scanned} scanned`);
                if (result.hidden_count > 0) {
                  // Apply the changes locally to avoid a full reload.
                  setEntities(prev => prev.map(e => {
                    const hidden = result.hidden.filter(h => h.entity_id === e.entity_id).map(h => h.field);
                    if (!hidden.length) return e;
                    return { ...e, attributes: (e.attributes || []).map(a => hidden.includes(a.name) ? { ...a, semantic_type: 'hidden' } : a) };
                  }));
                  setLastUndo({ label: 'Hide Empty Fields', snapshot, at: Date.now() });
                }
              } catch (err) { toast.error(err.message || 'Scan failed'); }
              finally { setHidingEmpty(false); }
            }}
            className="rounded-lg text-[10px] h-7 gap-1"
            title="Query each field and hide any with only 0 or 1 distinct value"
            data-testid="hide-empty-btn"
          >
            <EyeOff className="w-3 h-3" />
            {hidingEmpty ? 'Scanning…' : 'Hide Empty Fields'}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => setGroupsOpen(true)}
            className="rounded-lg text-[10px] h-7 gap-1"
            title="Group fields into business bundles (Premiums, Claims, …) for one-click focus"
            data-testid="field-groups-btn"
          >
            <Layers className="w-3 h-3" />
            Groups
            {(model?.field_groups || []).length > 0 && (
              <Badge variant="outline" className="text-[8px] h-3.5 px-1 py-0 ml-0.5">{model.field_groups.length}</Badge>
            )}
          </Button>
          <Badge variant="outline" className="text-[9px] h-5">{filteredRows.length} / {allRows.length} fields</Badge>
          {lastUndo && (
            <Button
              variant="outline" size="sm" disabled={undoBusy}
              onClick={undoLast}
              className="rounded-lg text-[10px] h-7 gap-1 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              title={`Undo the last ${lastUndo.label}`}
              data-testid="undo-last-btn"
            >
              <Undo2 className="w-3 h-3" />
              {undoBusy ? 'Undoing…' : `Undo ${lastUndo.label}`}
            </Button>
          )}
        </div>
      </div>

      {/* Field-group chips — one-click focus on business bundles */}
      {(model?.field_groups || []).length > 0 && (
        <div className="px-4 py-1 border-b border-border/20 flex items-center gap-1.5 flex-wrap bg-muted/5" data-testid="field-groups-chips">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Focus:</span>
          <button
            onClick={() => setActiveGroupId(null)}
            className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${activeGroupId === null ? 'bg-primary/20 text-primary border-primary/30' : 'text-muted-foreground border-border/25 hover:bg-muted/20'}`}
            data-testid="chip-all"
          >All</button>
          {model.field_groups.map(g => (
            <button
              key={g.group_id}
              onClick={() => setActiveGroupId(activeGroupId === g.group_id ? null : g.group_id)}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${activeGroupId === g.group_id ? 'bg-primary/20 text-primary border-primary/30' : 'text-muted-foreground border-border/25 hover:bg-muted/20'}`}
              data-testid={`chip-${g.group_id}`}
            >
              {g.color && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: g.color }} />}
              {g.name}
              <span className="ml-1 text-[9px] opacity-60">{(g.fields || []).length}</span>
            </button>
          ))}
        </div>
      )}

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="px-4 py-1.5 border-b border-primary/20 bg-primary/5 flex items-center gap-3" data-testid="bulk-bar">
          <Badge className="bg-primary/20 text-primary border-primary/30 text-[9px]">{selected.size} selected</Badge>
          <span className="text-[9px] text-muted-foreground">Set:</span>
          <Select value={bulkSemType} onValueChange={v => { setBulkSemType(v); applyBulk('semantic_type', v); }}>
            <SelectTrigger className="h-6 w-28 text-[9px] rounded bg-muted/30 border-border/30"><SelectValue placeholder="Sem. Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dimension">Dimension</SelectItem>
              <SelectItem value="measure">Measure</SelectItem>
              <SelectItem value="hidden">Hidden</SelectItem>
            </SelectContent>
          </Select>
          <Select value={bulkAgg} onValueChange={v => { setBulkAgg(v); applyBulk('aggregation', v); }}>
            <SelectTrigger className="h-6 w-28 text-[9px] rounded bg-muted/30 border-border/30"><SelectValue placeholder="Aggregation" /></SelectTrigger>
            <SelectContent>{AGG_OPTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={bulkFormat} onValueChange={v => { setBulkFormat(v); applyBulk('format', v); }}>
            <SelectTrigger className="h-6 w-28 text-[9px] rounded bg-muted/30 border-border/30"><SelectValue placeholder="Format" /></SelectTrigger>
            <SelectContent>{FORMAT_OPTIONS.map(f => <SelectItem key={f || '_none'} value={f || '_none'}>{f || '(none)'}</SelectItem>)}</SelectContent>
          </Select>
          <div className="border-l border-border/30 h-5 mx-1" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" onClick={aiDescribeSelected} disabled={aiDescLoading} className="rounded-lg text-[10px] h-6 gap-1" data-testid="ai-describe-selected">
                {aiDescLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                AI Describe Selected
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[240px]"><p className="text-xs">Send the selected fields with sample data to AI to auto-generate business-friendly descriptions</p></TooltipContent>
          </Tooltip>
          <button onClick={() => setSelected(new Set())} className="text-[9px] text-muted-foreground hover:text-foreground ml-auto"><X className="w-3 h-3" /></button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-[11px]" data-testid="metadata-grid">          <thead className="sticky top-0 z-20" style={{ background: 'hsl(var(--background))' }}>
            <tr className="border-b border-border/30" style={{ background: 'hsl(var(--background))' }}>
              <th className="w-8 px-2 py-2">
                <Checkbox checked={selected.size === filteredRows.length && filteredRows.length > 0} onCheckedChange={toggleAll} data-testid="meta-select-all" />
              </th>
              <th className="px-2 py-2 text-left w-32"><SortHeader col="name" label="Field Name" /></th>
              <th className="px-2 py-2 text-left w-32">Display Name</th>
              <th className="px-2 py-2 text-left w-16"><SortHeader col="data_type" label="Type" /></th>
              <th className="px-2 py-2 text-left w-24"><SortHeader col="semantic_type" label="Semantic" /></th>
              <th className="px-2 py-2 text-left w-24">Aggregation</th>
              <th className="px-2 py-2 text-left w-20">Format</th>
              <th className="px-2 py-2 text-left" style={{ minWidth: 180 }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {groupedByEntity.map(group => {
              const isCollapsed = collapsed.has(group.entityId);
              const entityFieldsSelected = group.rows.filter(r => selected.has(rowKey(r))).length;
              const allEntitySelected = entityFieldsSelected === group.rows.length && group.rows.length > 0;
              const entity = entities.find(e => e.entity_id === group.entityId);

              return (
                <React.Fragment key={group.entityId}>
                  {/* Entity group header row */}
                  <tr className="border-b border-border/20 sticky top-[33px] z-[10]" style={{ background: 'hsl(var(--muted))' }} data-testid={`entity-group-${group.entityId}`} onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, entityId: group.entityId, entityName: group.entityName }); }}>
                    <td className="px-2 py-1.5">
                      <Checkbox
                        checked={allEntitySelected}
                        onCheckedChange={v => toggleEntityRows(group.entityId, v)}
                        data-testid={`entity-check-${group.entityId}`}
                      />
                    </td>
                    <td colSpan={COL_SPAN - 1} className="px-1 py-1.5">
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleCollapse(group.entityId)} className="flex items-center gap-1 hover:text-foreground transition-colors" data-testid={`toggle-${group.entityId}`}>
                          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                          <span className="text-[11px] font-semibold text-foreground">{group.entityName}</span>
                        </button>
                        <Badge variant="outline" className="text-[8px] h-4">{group.rows.length} fields</Badge>
                        {entityFieldsSelected > 0 && <Badge className="text-[7px] bg-primary/15 text-primary border-primary/20 h-4">{entityFieldsSelected} selected</Badge>}
                        <span className="text-[9px] text-muted-foreground/70 mx-1">Display:</span>
                        <input
                          defaultValue={group.entityLogicalName || ''}
                          onBlur={e => { if (e.target.value !== (group.entityLogicalName || '')) updateEntityDisplayName(group.entityId, e.target.value); }}
                          placeholder={parseFieldName(group.entityName)}
                          className="bg-transparent border-b border-transparent hover:border-border/30 focus:border-primary/50 outline-none text-[10px] py-0.5 px-1 text-foreground placeholder:text-muted-foreground/30 w-40"
                          data-testid={`entity-display-${group.entityId}`}
                        />
                      </div>
                    </td>
                  </tr>
                  {/* Field rows */}
                  {!isCollapsed && group.rows.map(row => {
                    const key = rowKey(row);
                    const isSaving = saving.has(key);
                    const Icon = TYPE_ICONS[row.data_type] || Type;
                    const semColor = SEM_COLORS[row.semantic_type] || SEM_COLORS.dimension;
                    const isSelected = selected.has(key);
                    return (
                      <tr key={key} className={`border-b border-border/10 transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/10'} ${isSaving ? 'opacity-60' : ''}`} data-testid={`meta-row-${row.name}`}>
                        <td className="px-2 py-1.5">
                          <Checkbox checked={isSelected} onCheckedChange={() => setSelected(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; })} />
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1.5 pl-3">
                            <Icon className="w-3 h-3 text-muted-foreground/70 flex-shrink-0" />
                            <span className="font-mono text-foreground text-[10px] truncate" title={row.name}>{row.name}</span>
                            {row.is_calculated && <Badge className="text-[7px] bg-amber-500/15 text-amber-400 border-amber-500/20 h-3.5 px-1">calc</Badge>}
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            key={`bname-${row.entityId}-${row.attrIndex}-${row.business_name || ''}`}
                            defaultValue={row.business_name || ''}
                            onBlur={e => handleBlurSave(row.entityId, row.attrIndex, 'business_name', e.target.value)}
                            placeholder={parseFieldName(row.name)}
                            className="w-full bg-transparent border-b border-transparent hover:border-border/30 focus:border-primary/50 outline-none text-[10px] py-0.5 px-1 text-foreground placeholder:text-muted-foreground/30 transition-colors"
                            data-testid={`display-name-${row.entityId}-${row.attrIndex}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Select value={row.data_type || 'string'} onValueChange={v => updateField(row.entityId, row.attrIndex, { data_type: v })}>
                            <SelectTrigger className="h-6 text-[9px] rounded bg-muted/20 border-border/20 px-1.5" data-testid={`data-type-${row.entityId}-${row.attrIndex}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{DATA_TYPE_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1.5">
                          <Select value={row.semantic_type || 'dimension'} onValueChange={v => updateField(row.entityId, row.attrIndex, { semantic_type: v })}>
                            <SelectTrigger className={`h-6 text-[9px] rounded border px-1.5 ${semColor}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="dimension">dimension</SelectItem>
                              <SelectItem value="measure">measure</SelectItem>
                              <SelectItem value="hidden">hidden</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1.5">
                          {row.semantic_type === 'measure' ? (
                            <Select value={row.aggregation || 'sum'} onValueChange={v => updateField(row.entityId, row.attrIndex, { aggregation: v })}>
                              <SelectTrigger className="h-6 text-[9px] rounded bg-muted/20 border-border/20 px-1.5"><SelectValue /></SelectTrigger>
                              <SelectContent>{AGG_OPTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : <span className="text-muted-foreground/30 text-[9px]">—</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          <Select value={row.format || '_none'} onValueChange={v => updateField(row.entityId, row.attrIndex, { format: v === '_none' ? '' : v })}>
                            <SelectTrigger className="h-6 text-[9px] rounded bg-muted/20 border-border/20 px-1.5 font-mono"><SelectValue /></SelectTrigger>
                            <SelectContent>{formatOptionsFor(row.data_type).map(f => <SelectItem key={f || '_none'} value={f || '_none'}><span className="flex items-center justify-between gap-3 w-full"><span className="font-mono">{f || '(none)'}</span>{f && <span className="text-[9px] text-muted-foreground/80 tabular-nums">{formatSample(f)}</span>}</span></SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            key={`desc-${row.entityId}-${row.attrIndex}-${row.description || ''}`}
                            defaultValue={row.description || ''}
                            onBlur={e => handleBlurSave(row.entityId, row.attrIndex, 'description', e.target.value)}
                            placeholder="Enter description..."
                            className={`w-full bg-transparent border-b border-transparent hover:border-border/30 focus:border-primary/50 outline-none text-[10px] py-0.5 px-1 text-foreground placeholder:text-muted-foreground/30 transition-colors ${!row.description?.trim() ? 'text-muted-foreground/70 italic' : ''}`}
                            data-testid={`desc-${row.entityId}-${row.attrIndex}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {filteredRows.length === 0 && (
              <tr><td colSpan={COL_SPAN} className="text-center py-12 text-muted-foreground text-sm">No fields match your filters</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <ModelSummaryFooter calcMeasures={calcMeasures} setCalcMeasures={setCalcMeasures} modelGlossary={modelGlossary} setModelGlossary={setModelGlossary} entities={entities} setEntities={setEntities} modelId={modelId} />
      {groupsOpen && (
        <FieldGroupsModal
          modelId={modelId}
          model={model}
          setModel={setModel}
          entities={entities}
          onClose={() => setGroupsOpen(false)}
        />
      )}
      {ctxMenu && (
        <div
          className="fixed z-[100] min-w-[180px] rounded-lg border border-border/60 bg-popover shadow-xl py-1"
          style={{ top: ctxMenu.y, left: ctxMenu.x }}
          onClick={(e) => e.stopPropagation()}
          data-testid="metadata-entity-context-menu"
        >
          <div className="px-3 py-1 text-[9px] uppercase tracking-wider text-muted-foreground truncate">{ctxMenu.entityName}</div>
          <button
            onClick={() => handleDeleteEntity(ctxMenu.entityId, ctxMenu.entityName)}
            disabled={deletingEntityId === ctxMenu.entityId}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
            data-testid="metadata-delete-table-btn"
          >
            <X className="w-3.5 h-3.5" /> Delete table from model
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Field Groups modal — CRUD business bundles for a semantic model.
 *
 * Each group carries a name, an optional colour, and a list of
 * fully-qualified `entity.field` strings. Groups become one-tap focus
 * chips above the metadata table.
 */
function FieldGroupsModal({ modelId, model, setModel, entities, onClose }) {
  const [draft, setDraft] = useState(() => (model?.field_groups || []).map(g => ({ ...g, fields: [...(g.fields || [])] })));
  const [saving, setSaving] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const allFields = useMemo(() =>
    entities.flatMap(e => (e.attributes || [])
      .filter(a => (a.semantic_type || '').toLowerCase() !== 'hidden')
      .map(a => ({ key: `${e.name}.${a.name}`, entity: e.name, field: a.name, semantic: a.semantic_type }))),
    [entities]
  );

  const addGroup = () => {
    const g = { group_id: `grp_${Math.random().toString(36).slice(2, 10)}`, name: 'New Group', color: '#3b82f6', fields: [] };
    setDraft(prev => [...prev, g]);
    setActiveIdx(draft.length);
  };
  const removeGroup = (idx) => {
    setDraft(prev => prev.filter((_, i) => i !== idx));
    setActiveIdx(0);
  };
  const patchGroup = (idx, patch) => setDraft(prev => prev.map((g, i) => i === idx ? { ...g, ...patch } : g));
  const toggleField = (idx, key) => setDraft(prev => prev.map((g, i) => {
    if (i !== idx) return g;
    const set = new Set(g.fields);
    if (set.has(key)) set.delete(key); else set.add(key);
    return { ...g, fields: [...set] };
  }));

  const save = async () => {
    setSaving(true);
    try {
      const result = await api.updateFieldGroups(modelId, draft);
      if (setModel) setModel(prev => ({ ...(prev || {}), field_groups: result.field_groups }));
      toast.success('Field groups saved');
      onClose();
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const active = draft[activeIdx];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="field-groups-modal">
      <div onClick={e => e.stopPropagation()} className="w-full max-w-4xl bg-background border border-border/40 rounded-xl shadow-xl flex flex-col overflow-hidden" style={{ maxHeight: '80vh' }}>
        <div className="px-4 py-3 border-b border-border/30 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Field Groups</h3>
            <p className="text-[10px] text-muted-foreground">Bundle related fields into business focus areas like Premiums or Claims.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="field-groups-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 flex overflow-hidden">
          {/* Left rail: group list */}
          <div className="w-52 border-r border-border/30 overflow-auto py-2">
            {draft.map((g, i) => (
              <button
                key={g.group_id}
                onClick={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-1.5 text-[11px] flex items-center gap-2 ${i === activeIdx ? 'bg-primary/10 border-l-2 border-primary' : 'hover:bg-muted/20 border-l-2 border-transparent'}`}
                data-testid={`fg-tab-${g.group_id}`}
              >
                {g.color && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: g.color }} />}
                <span className="truncate flex-1">{g.name || '(unnamed)'}</span>
                <span className="text-[9px] text-muted-foreground/70">{g.fields.length}</span>
              </button>
            ))}
            <button onClick={addGroup} className="w-full text-left px-3 py-1.5 text-[11px] text-primary hover:bg-primary/10" data-testid="fg-add-group">+ Add group</button>
          </div>
          {/* Right pane: editor */}
          <div className="flex-1 overflow-auto p-4 space-y-3">
            {!active ? (
              <p className="text-[11px] text-muted-foreground italic">No group selected. Click <b>Add group</b> to start.</p>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-muted-foreground w-14 shrink-0">Name</label>
                  <Input value={active.name} onChange={e => patchGroup(activeIdx, { name: e.target.value })}
                    className="h-7 text-[11px] flex-1" data-testid="fg-name" />
                  <label className="text-[10px] text-muted-foreground shrink-0">Color</label>
                  <input type="color" value={active.color || '#3b82f6'}
                    onChange={e => patchGroup(activeIdx, { color: e.target.value })}
                    className="h-7 w-8 rounded border border-border/30 bg-transparent cursor-pointer"
                    data-testid="fg-color" />
                  <button onClick={() => removeGroup(activeIdx)}
                    className="text-[10px] px-2 py-1 rounded border border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                    data-testid="fg-delete">Delete group</button>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Fields in group ({active.fields.length})</p>
                    <div className="flex items-center gap-2">
                      <button onClick={() => patchGroup(activeIdx, { fields: allFields.map(f => f.key) })}
                        className="text-[9px] text-primary" data-testid="fg-select-all">Select all</button>
                      <button onClick={() => patchGroup(activeIdx, { fields: [] })}
                        className="text-[9px] text-muted-foreground" data-testid="fg-clear">Clear</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-96 overflow-auto border border-border/20 rounded p-2 bg-muted/10">
                    {allFields.map(f => {
                      const on = active.fields.includes(f.key);
                      return (
                        <label key={f.key} className={`flex items-center gap-1.5 text-[10px] px-1.5 py-0.5 rounded cursor-pointer ${on ? 'bg-primary/15 text-primary' : 'hover:bg-muted/30'}`} data-testid={`fg-field-${f.key}`}>
                          <input type="checkbox" checked={on} onChange={() => toggleField(activeIdx, f.key)} className="scale-75" />
                          <span className="truncate flex-1"><span className="text-muted-foreground/70">{f.entity}.</span>{f.field}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border/30 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} className="h-7 text-[10px]" data-testid="fg-cancel">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} className="h-7 text-[10px]" data-testid="fg-save">{saving ? 'Saving…' : 'Save groups'}</Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact accordion showing model-level Calculated Measures, Calculated Dimensions
 * and Glossary — so the single-screen metadata view stays audit-friendly without
 * forcing the user back to the tabbed sidebar.
 */
function ModelSummaryFooter({ calcMeasures, setCalcMeasures, modelGlossary, setModelGlossary, entities, setEntities, modelId }) {
  const [open, setOpen] = useState('measures');
  const [busy, setBusy] = useState(null);
  const [defOpen, setDefOpen] = useState(null); // measure_id for definition prompt
  const [defText, setDefText] = useState('');
  // Inline edit for footer records — one record at a time
  const [editing, setEditing] = useState(null); // { kind, id, draft }
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (kind, id, draft) => setEditing({ kind, id, draft: { ...draft } });
  const cancelEdit = () => { if (!editSaving) setEditing(null); };
  const updateDraft = (patch) => setEditing(e => ({ ...e, draft: { ...e.draft, ...patch } }));

  const saveMeasureEdit = async () => {
    const m = editing.draft;
    setEditSaving(true);
    try {
      const payload = {
        description: m.description || '',
      };
      if (m._isScoped) {
        payload.base_field = m.base_field || '';
        payload.aggregation = m.aggregation || 'sum';
        // Parse the scope filters textarea back into a list
        try {
          const parsed = JSON.parse(m._scopeFiltersText || '[]');
          if (Array.isArray(parsed)) payload.scope_filters = parsed;
        } catch (err) { throw new Error('Scope filters must be valid JSON array'); }
      } else {
        payload.formula = m.formula || '';
      }
      const updated = await api.updateSemanticMeasure(editing.id, payload);
      if (setCalcMeasures) {
        setCalcMeasures(prev => prev.map(x => x.measure_id === editing.id ? { ...x, ...updated } : x));
      }
      toast.success('Saved');
      setEditing(null);
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setEditSaving(false); }
  };

  const saveDimEdit = async () => {
    const d = editing.draft;
    setEditSaving(true);
    try {
      const entity = entities.find(e => e.entity_id === d._entityId);
      if (!entity) throw new Error('Entity not found');
      const nextAttrs = (entity.attributes || []).map(a =>
        a.name === d.name ? { ...a, formula: d.formula || '', display_name: d.display_name || '', description: d.description || '' } : a
      );
      const updated = await api.updateSemanticEntity(entity.entity_id, { attributes: nextAttrs });
      setEntities(prev => prev.map(e => e.entity_id === entity.entity_id ? { ...e, attributes: updated.attributes || nextAttrs } : e));
      toast.success('Saved');
      setEditing(null);
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setEditSaving(false); }
  };

  const saveGlossaryEdit = async () => {
    const g = editing.draft;
    setEditSaving(true);
    try {
      const payload = { term: g.term || '', definition: g.definition || '', category: g.category || '' };
      const updated = await api.updateGlossaryTerm(editing.id, payload);
      if (setModelGlossary) {
        setModelGlossary(prev => prev.map(t => (t.term_id || t.term) === editing.id ? { ...t, ...updated } : t));
      }
      toast.success('Saved');
      setEditing(null);
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setEditSaving(false); }
  };

  // Resizable panel height — persisted so the user's preference sticks
  // across reloads. Clamped to keep both the table and footer usable.
  const MIN_H = 80;
  const MAX_H = 600;
  const [panelHeight, setPanelHeight] = useState(() => {
    if (typeof window === 'undefined') return 224;
    const stored = parseInt(window.localStorage.getItem('inzara.metadata.footerHeight') || '', 10);
    return Number.isFinite(stored) ? Math.min(MAX_H, Math.max(MIN_H, stored)) : 224;
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startY: 0, startH: 0 });

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const delta = dragRef.current.startY - clientY;
      const next = Math.min(MAX_H, Math.max(MIN_H, dragRef.current.startH + delta));
      setPanelHeight(next);
    };
    const onUp = () => {
      setDragging(false);
      try { window.localStorage.setItem('inzara.metadata.footerHeight', String(panelHeight)); } catch (_) { /* ignore quota */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, panelHeight]);

  const beginDrag = (e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { startY: clientY, startH: panelHeight };
    setDragging(true);
    e.preventDefault();
  };

  const scoped = calcMeasures.filter(m => (m.scope_filters || []).length > 0);
  const formulaMeasures = calcMeasures.filter(m => !(m.scope_filters || []).length && m.formula);
  const calcDimAttrs = entities.flatMap(e => (e.attributes || []).filter(a => a.is_calculated && a.semantic_type === 'dimension').map(a => ({ ...a, entityName: e.name, entityId: e.entity_id })));
  const sections = [
    { id: 'measures', label: 'Calculated Measures', count: calcMeasures.length },
    { id: 'aggregates', label: 'Calculated Aggregates', count: scoped.length },
    { id: 'dimensions', label: 'Calculated Dimensions', count: calcDimAttrs.length },
    { id: 'glossary', label: 'Model Glossary', count: (modelGlossary || []).length },
  ];
  return (
    <div className="border-t border-border/40 shrink-0" data-testid="model-summary-footer">
      {open && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize summary panel — drag up or down"
          onMouseDown={beginDrag}
          onTouchStart={beginDrag}
          onDoubleClick={() => { setPanelHeight(224); try { window.localStorage.setItem('inzara.metadata.footerHeight', '224'); } catch (_) { /* ignore */ } }}
          className={`group relative h-1.5 cursor-row-resize select-none ${dragging ? 'bg-primary/40' : 'bg-border/30 hover:bg-primary/30'} transition-colors`}
          data-testid="footer-resize-handle"
          title="Drag to resize · Double-click to reset"
        >
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-0.5 rounded-full bg-muted-foreground/50 group-hover:bg-primary/60" />
        </div>
      )}
      <div className="flex items-center gap-1 px-4 py-1.5 bg-muted/10 overflow-x-auto">
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => setOpen(open === s.id ? null : s.id)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-medium border transition-colors ${open === s.id ? 'bg-primary/15 text-primary border-primary/25' : 'text-muted-foreground hover:text-foreground border-border/25 hover:bg-muted/20'}`}
            data-testid={`summary-tab-${s.id}`}
          >
            {s.label}
            <Badge variant="outline" className="text-[8px] h-3.5 px-1 py-0">{s.count}</Badge>
          </button>
        ))}
      </div>
      {open && (
        <div
          style={{ height: panelHeight }}
          className="overflow-auto px-4 py-2 border-t border-border/20"
          data-testid={`summary-panel-${open}`}
        >
          {open === 'measures' && (
            formulaMeasures.length + scoped.length === 0 ? (
              <p className="text-[10px] italic text-muted-foreground/70">No calculated measures on this model.</p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {[...formulaMeasures, ...scoped].map(m => {
                  const cert = m.certification || {};
                  const status = cert.status || 'draft';
                  const isEditing = editing && editing.kind === 'measure' && editing.id === m.measure_id;
                  const isScoped = (m.scope_filters || []).length > 0;
                  return (
                    <li key={m.measure_id} className="p-1.5 rounded bg-muted/15 border border-border/20 flex flex-col gap-1" data-testid={`summary-measure-${m.measure_id}`}>
                      {!isEditing ? (
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] font-medium text-foreground">{m.name}</span>
                              <CertificationBadge status={status} size="xs" replacedBy={cert.replaced_by} testId={`measure-cert-${m.measure_id}`} />
                            </div>
                            <div className="text-[9px] font-mono text-muted-foreground/80 truncate">
                              {m.formula || (m.scope_filters ? `${(m.aggregation || 'sum').toUpperCase()}(${m.base_field || '?'}) where ${(m.scope_filters || []).map(f => `${f.field} ${f.operator} ${JSON.stringify(f.value ?? '')}`).join(' AND ')}` : '')}
                            </div>
                            {m.description && (
                              <div className="text-[9px] text-muted-foreground mt-0.5">{m.description}</div>
                            )}
                            {cert.business_definition && (
                              <div className="text-[9px] text-muted-foreground mt-0.5 italic">{cert.business_definition}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => openEdit('measure', m.measure_id, {
                                formula: m.formula || '', description: m.description || '',
                                base_field: m.base_field || '', aggregation: m.aggregation || 'sum',
                                _isScoped: isScoped,
                                _scopeFiltersText: JSON.stringify(m.scope_filters || [], null, 2),
                              })}
                              className="text-[9px] px-1.5 py-0.5 rounded border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/30"
                              data-testid={`edit-measure-${m.measure_id}`}
                            >
                              Edit
                            </button>
                            {status === 'draft' && defOpen !== m.measure_id && (
                              <button
                                onClick={() => { setDefOpen(m.measure_id); setDefText(''); }}
                                className="text-[9px] px-1.5 py-0.5 rounded border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                                data-testid={`submit-cert-${m.measure_id}`}
                              >
                                Certify
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1" data-testid={`edit-form-measure-${m.measure_id}`}>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-medium text-foreground">{m.name}</span>
                            <span className="text-[9px] text-muted-foreground">{isScoped ? 'scoped aggregate' : 'formula measure'}</span>
                          </div>
                          {isScoped ? (
                            <>
                              <div className="flex items-center gap-1">
                                <label className="text-[9px] text-muted-foreground w-16 shrink-0">Aggregation</label>
                                <Select value={editing.draft.aggregation} onValueChange={v => updateDraft({ aggregation: v })}>
                                  <SelectTrigger className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1" data-testid={`edit-agg-${m.measure_id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>{['sum','avg','count','count_distinct','min','max'].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <div className="flex items-center gap-1">
                                <label className="text-[9px] text-muted-foreground w-16 shrink-0">Base field</label>
                                <Input value={editing.draft.base_field} onChange={e => updateDraft({ base_field: e.target.value })}
                                  className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1"
                                  data-testid={`edit-base-field-${m.measure_id}`} />
                              </div>
                              <div>
                                <label className="text-[9px] text-muted-foreground">Scope filters (JSON)</label>
                                <textarea value={editing.draft._scopeFiltersText}
                                  onChange={e => updateDraft({ _scopeFiltersText: e.target.value })}
                                  rows={3}
                                  className="w-full text-[9px] font-mono rounded bg-muted/30 border border-border/30 p-1"
                                  data-testid={`edit-scope-filters-${m.measure_id}`} />
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center gap-1">
                              <label className="text-[9px] text-muted-foreground w-16 shrink-0">Formula</label>
                              <Input value={editing.draft.formula} onChange={e => updateDraft({ formula: e.target.value })}
                                className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1 font-mono"
                                data-testid={`edit-formula-${m.measure_id}`} />
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-16 shrink-0">Description</label>
                            <Input value={editing.draft.description} onChange={e => updateDraft({ description: e.target.value })}
                              className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1"
                              data-testid={`edit-desc-${m.measure_id}`} />
                          </div>
                          <div className="flex items-center justify-end gap-1 pt-1">
                            <button onClick={cancelEdit} disabled={editSaving}
                              className="text-[9px] px-2 py-0.5 rounded border border-border/30 text-muted-foreground hover:text-foreground">Cancel</button>
                            <button onClick={saveMeasureEdit} disabled={editSaving}
                              className="text-[9px] px-2 py-0.5 rounded bg-primary text-primary-foreground disabled:opacity-40"
                              data-testid={`save-measure-${m.measure_id}`}>{editSaving ? 'Saving…' : 'Save'}</button>
                          </div>
                        </div>
                      )}
                      {defOpen === m.measure_id && (
                        <div className="flex items-center gap-1 mt-1">
                          <input type="text" value={defText} onChange={e => setDefText(e.target.value)}
                            placeholder="Business definition"
                            className="h-6 flex-1 px-1.5 rounded bg-muted/30 border border-border/30 text-[10px]"
                            data-testid={`cert-biz-def-${m.measure_id}`} />
                          <button
                            disabled={busy === m.measure_id || !defText.trim()}
                            onClick={async () => {
                              setBusy(m.measure_id);
                              try {
                                await api.submitMeasureCertification(m.measure_id, { business_definition: defText.trim() });
                                toast.success('Submitted for certification');
                                setDefOpen(null); setDefText('');
                                window.location.reload();
                              } catch (err) { toast.error(err.message || 'Submit failed'); }
                              finally { setBusy(null); }
                            }}
                            className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500 text-white disabled:opacity-40"
                            data-testid={`cert-biz-def-submit-${m.measure_id}`}>Submit</button>
                          <button onClick={() => { setDefOpen(null); setDefText(''); }}
                            className="text-[9px] px-1 py-0.5 text-muted-foreground hover:text-foreground">×</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )
          )}
          {open === 'aggregates' && (
            scoped.length === 0 ? (
              <p className="text-[10px] italic text-muted-foreground/70">No calculated aggregate filters defined. Use &quot;+ Measure&quot; and switch to Scoped mode to add one.</p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {scoped.map(m => {
                  const isEditing = editing && editing.kind === 'measure' && editing.id === m.measure_id;
                  return (
                    <li key={m.measure_id} className="p-1.5 rounded bg-muted/15 border border-border/20" data-testid={`summary-aggregate-${m.measure_id}`}>
                      {!isEditing ? (
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-medium text-foreground">{m.name}</div>
                            <div className="text-[9px] font-mono text-muted-foreground/80 mt-0.5">
                              {(m.aggregation || 'sum').toUpperCase()}({m.base_field || '?'})
                              {' '}where{' '}
                              {(m.scope_filters || []).map((f, i) => (
                                <span key={i}>{i > 0 && <span className="text-primary/80"> AND </span>}<span>{f.field} {f.operator}{f.value !== undefined ? ` ${JSON.stringify(f.value)}` : ''}</span></span>
                              ))}
                            </div>
                            {m.description && <div className="text-[9px] text-muted-foreground mt-0.5">{m.description}</div>}
                          </div>
                          <button
                            onClick={() => openEdit('measure', m.measure_id, {
                              formula: m.formula || '', description: m.description || '',
                              base_field: m.base_field || '', aggregation: m.aggregation || 'sum',
                              _isScoped: true,
                              _scopeFiltersText: JSON.stringify(m.scope_filters || [], null, 2),
                            })}
                            className="text-[9px] px-1.5 py-0.5 rounded border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/30 shrink-0"
                            data-testid={`edit-aggregate-${m.measure_id}`}
                          >
                            Edit
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1" data-testid={`edit-form-aggregate-${m.measure_id}`}>
                          <div className="text-[10px] font-medium text-foreground">{m.name}</div>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-16 shrink-0">Aggregation</label>
                            <Select value={editing.draft.aggregation} onValueChange={v => updateDraft({ aggregation: v })}>
                              <SelectTrigger className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1"><SelectValue /></SelectTrigger>
                              <SelectContent>{['sum','avg','count','count_distinct','min','max'].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-16 shrink-0">Base field</label>
                            <Input value={editing.draft.base_field} onChange={e => updateDraft({ base_field: e.target.value })}
                              className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1" />
                          </div>
                          <div>
                            <label className="text-[9px] text-muted-foreground">Scope filters (JSON)</label>
                            <textarea value={editing.draft._scopeFiltersText}
                              onChange={e => updateDraft({ _scopeFiltersText: e.target.value })}
                              rows={3}
                              className="w-full text-[9px] font-mono rounded bg-muted/30 border border-border/30 p-1" />
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-16 shrink-0">Description</label>
                            <Input value={editing.draft.description} onChange={e => updateDraft({ description: e.target.value })}
                              className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1" />
                          </div>
                          <div className="flex items-center justify-end gap-1 pt-1">
                            <button onClick={cancelEdit} disabled={editSaving}
                              className="text-[9px] px-2 py-0.5 rounded border border-border/30 text-muted-foreground hover:text-foreground">Cancel</button>
                            <button onClick={saveMeasureEdit} disabled={editSaving}
                              className="text-[9px] px-2 py-0.5 rounded bg-primary text-primary-foreground disabled:opacity-40"
                              data-testid={`save-aggregate-${m.measure_id}`}>{editSaving ? 'Saving…' : 'Save'}</button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )
          )}
          {open === 'dimensions' && (
            calcDimAttrs.length === 0 ? (
              <p className="text-[10px] italic text-muted-foreground/70">No calculated dimensions on this model.</p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {calcDimAttrs.map(a => {
                  const key = `${a.entityId}.${a.name}`;
                  const isEditing = editing && editing.kind === 'dim' && editing.id === key;
                  return (
                    <li key={key} className="p-1.5 rounded bg-muted/15 border border-border/20" data-testid={`summary-dim-${a.name}`}>
                      {!isEditing ? (
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-medium text-foreground">{a.display_name || a.name} <span className="text-muted-foreground/80 text-[9px]">on {a.entityName}</span></div>
                            <div className="text-[9px] font-mono text-muted-foreground/80 truncate">{a.formula || '—'}</div>
                            {a.description && <div className="text-[9px] text-muted-foreground mt-0.5">{a.description}</div>}
                          </div>
                          <button
                            onClick={() => openEdit('dim', key, {
                              name: a.name, _entityId: a.entityId,
                              display_name: a.display_name || '',
                              formula: a.formula || '',
                              description: a.description || '',
                            })}
                            className="text-[9px] px-1.5 py-0.5 rounded border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/30 shrink-0"
                            data-testid={`edit-dim-${a.name}`}
                          >
                            Edit
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1" data-testid={`edit-form-dim-${a.name}`}>
                          <div className="text-[10px] font-medium text-foreground">{a.name}</div>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-20 shrink-0">Display name</label>
                            <Input value={editing.draft.display_name} onChange={e => updateDraft({ display_name: e.target.value })}
                              className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1" />
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-20 shrink-0">Formula</label>
                            <Input value={editing.draft.formula} onChange={e => updateDraft({ formula: e.target.value })}
                              className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1 font-mono" />
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-20 shrink-0">Description</label>
                            <Input value={editing.draft.description} onChange={e => updateDraft({ description: e.target.value })}
                              className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1" />
                          </div>
                          <div className="flex items-center justify-end gap-1 pt-1">
                            <button onClick={cancelEdit} disabled={editSaving}
                              className="text-[9px] px-2 py-0.5 rounded border border-border/30 text-muted-foreground hover:text-foreground">Cancel</button>
                            <button onClick={saveDimEdit} disabled={editSaving}
                              className="text-[9px] px-2 py-0.5 rounded bg-primary text-primary-foreground disabled:opacity-40"
                              data-testid={`save-dim-${a.name}`}>{editSaving ? 'Saving…' : 'Save'}</button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )
          )}
          {open === 'glossary' && (
            (modelGlossary || []).length === 0 ? (
              <p className="text-[10px] italic text-muted-foreground/70">Glossary is empty.</p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {(modelGlossary || []).map(t => {
                  const gid = t.term_id || t.term;
                  const isEditing = editing && editing.kind === 'glossary' && editing.id === gid;
                  return (
                    <li key={gid} className="p-1.5 rounded bg-muted/15 border border-border/20" data-testid={`summary-glossary-${gid}`}>
                      {!isEditing ? (
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-medium text-foreground">{t.term}</div>
                            <div className="text-[9px] text-muted-foreground/80 truncate">{t.definition}</div>
                            {t.category && <div className="text-[9px] text-muted-foreground/70">{t.category}</div>}
                          </div>
                          <button
                            onClick={() => openEdit('glossary', gid, {
                              term: t.term || '', definition: t.definition || '', category: t.category || '',
                            })}
                            className="text-[9px] px-1.5 py-0.5 rounded border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/30 shrink-0"
                            data-testid={`edit-glossary-${gid}`}
                          >
                            Edit
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1" data-testid={`edit-form-glossary-${gid}`}>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-16 shrink-0">Term</label>
                            <Input value={editing.draft.term} onChange={e => updateDraft({ term: e.target.value })}
                              className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1" />
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-16 shrink-0">Definition</label>
                            <Input value={editing.draft.definition} onChange={e => updateDraft({ definition: e.target.value })}
                              className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1" />
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px] text-muted-foreground w-16 shrink-0">Category</label>
                            <Input value={editing.draft.category} onChange={e => updateDraft({ category: e.target.value })}
                              className="h-6 text-[10px] rounded bg-muted/30 border-border/30 flex-1" />
                          </div>
                          <div className="flex items-center justify-end gap-1 pt-1">
                            <button onClick={cancelEdit} disabled={editSaving}
                              className="text-[9px] px-2 py-0.5 rounded border border-border/30 text-muted-foreground hover:text-foreground">Cancel</button>
                            <button onClick={saveGlossaryEdit} disabled={editSaving}
                              className="text-[9px] px-2 py-0.5 rounded bg-primary text-primary-foreground disabled:opacity-40"
                              data-testid={`save-glossary-${gid}`}>{editSaving ? 'Saving…' : 'Save'}</button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )
          )}
        </div>
      )}
    </div>
  );
}
